import type { AssetCheck } from '@/types/assetCheck'
import { formatDateTime } from '@/lib/format'
import { VerdictBadge } from './VerdictBadge'
import { DimensionRow } from './DimensionRow'
import { ReasoningList } from './ReasoningList'
import { ResultDetailAccordion } from './ResultDetailAccordion'

interface AssetCheckResultCardProps {
  check: AssetCheck
}

const DIMENSION_LABELS: Record<string, string> = {
  diversification: 'Diversifikation',
  strategy_conformance: 'Strategie-Konformität',
  risk_profile: 'Risiko-Profil',
  tax_situation: 'Steuer-Lage',
}

const DIMENSION_ORDER = ['diversification', 'strategy_conformance', 'risk_profile', 'tax_situation']

export function AssetCheckResultCard({ check }: AssetCheckResultCardProps) {
  return (
    <div
      className="space-y-4 rounded-lg border border-slate-200 bg-white p-4"
      data-testid="asset-check-result"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{check.asset.security_name}</h3>
          <p className="text-xs text-slate-500">
            {check.input.isin} · {formatDateTime(check.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <VerdictBadge verdict={check.verdict} />
          <span className="text-sm text-slate-600">Conviction {check.conviction}/5</span>
        </div>
      </div>

      <div className="divide-y divide-slate-100 border-y border-slate-100">
        {DIMENSION_ORDER.map((key) => {
          const rating = check.dimensions[key]
          if (!rating) return null
          return <DimensionRow key={key} label={DIMENSION_LABELS[key] ?? key} rating={rating} />
        })}
      </div>

      <ReasoningList reasoning={check.reasoning} />

      {check.warnings.length > 0 && (
        <div
          className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="result-warnings"
        >
          <strong className="block mb-1">Hinweise:</strong>
          <ul className="list-disc list-inside">
            {check.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <ResultDetailAccordion check={check} />
    </div>
  )
}
